import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "drawer", "list", "itemTemplate", "emptyTemplate"]
  static values = {
    conversationsUrl: { type: String, default: "/ai/conversations" },
    assistantUrl: { type: String, default: "/assistant" }
  }

  connect() {
    this._boundCloseOnEscape = this._closeOnEscape.bind(this)
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundCloseOnEscape)
  }

  async open() {
    if (!this.hasOverlayTarget || !this.hasDrawerTarget) return

    this.overlayTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      this.drawerTarget.classList.add("open")
    })
    document.addEventListener("keydown", this._boundCloseOnEscape)
    await this._loadHistory()
  }

  close() {
    if (!this.hasOverlayTarget || !this.hasDrawerTarget) return

    this.drawerTarget.classList.remove("open")
    this.overlayTarget.classList.add("hidden")
    document.removeEventListener("keydown", this._boundCloseOnEscape)
  }

  async selectConversation(event) {
    const sessionId = event.currentTarget.dataset.sessionId
    if (!sessionId) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    await fetch(`${this.conversationsUrlValue}/${sessionId}/activate`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrfToken }
    })

    this.close()
    window.Turbo ? window.Turbo.visit(this.assistantUrlValue) : (window.location.href = this.assistantUrlValue)
  }

  async deleteConversation(event) {
    event.stopPropagation()
    const sessionId = event.currentTarget.dataset.sessionId
    if (!sessionId) return

    if (!confirm("Supprimer cette conversation ? Elle ne sera plus accessible.")) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(`${this.conversationsUrlValue}/${sessionId}/request_deletion`, {
        method: "PATCH",
        headers: { "X-CSRF-Token": csrfToken }
      })

      const ids = this._getSessionIds().filter(id => id !== sessionId)
      localStorage.setItem("ai_session_ids", JSON.stringify(ids))

      await this._loadHistory()
    } catch (_) {
      // Ignore errors
    }
  }

  // --- Private ---

  async _loadHistory() {
    if (!this.hasListTarget) return

    const sessionIds = this._getSessionIds()
    if (sessionIds.length === 0) {
      this._showEmpty("Aucune conversation")
      return
    }

    this._showEmpty("Chargement...")

    try {
      const params = new URLSearchParams()
      sessionIds.forEach(id => params.append("session_ids[]", id))

      const response = await fetch(`${this.conversationsUrlValue}?${params}`, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) throw new Error("Failed to load")

      const conversations = await response.json()

      if (conversations.length === 0) {
        this._showEmpty("Aucune conversation")
        return
      }

      this.listTarget.replaceChildren()
      conversations.forEach(conv => {
        this.listTarget.appendChild(this._buildItem(conv))
      })
    } catch (_) {
      this._showEmpty("Erreur de chargement")
    }
  }

  _buildItem(conv) {
    const fragment = this.itemTemplateTarget.content.cloneNode(true)
    const item = fragment.querySelector("[data-history-item]")

    item.dataset.sessionId = conv.session_id

    const deleteBtn = item.querySelector("[data-history-delete]")
    if (deleteBtn) deleteBtn.dataset.sessionId = conv.session_id

    const msgEl = fragment.querySelector("[data-history-message]")
    if (msgEl) msgEl.textContent = conv.first_message || "Conversation sans titre"

    const dateEl = fragment.querySelector("[data-history-date]")
    if (dateEl) {
      const date = new Date(conv.updated_at)
      dateEl.textContent = `${date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} ${date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`
    }

    const countEl = fragment.querySelector("[data-history-count]")
    if (countEl) countEl.textContent = `${conv.message_count} msg`

    return fragment
  }

  _showEmpty(text) {
    this.listTarget.replaceChildren()
    const fragment = this.emptyTemplateTarget.content.cloneNode(true)
    const el = fragment.querySelector("[data-empty-text]")
    if (el) el.textContent = text
    this.listTarget.appendChild(fragment)
  }

  _getSessionIds() {
    try {
      return JSON.parse(localStorage.getItem("ai_session_ids") || "[]")
    } catch (_) {
      return []
    }
  }

  _closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }
}
